/**
 * A portfolio deal's properties (`GET /loans/{id}/portfolio`) and the two
 * routes that create them: `POST /ingestion/{id}/materialize-portfolio`, from
 * the names an intake package listed, and `POST /loans/{id}/properties`, from a
 * name a person types on the deal (the first one makes the deal a portfolio).
 *
 * The package's listed properties were read at intake and shown on no screen,
 * and the create route had no caller (stress test CMP-07). This is the supply
 * for the dossier panel that closes both gaps.
 */
import type { QueryClient } from '@tanstack/react-query'

import api from './api'
import { invalidateLoanQueries } from './loanQueryKeys'

export interface PortfolioChild {
  id: string
  property_name: string
  location: string
  stage: string
  deal_status?: string | null
}

export interface PortfolioSummary {
  parent_id: string
  is_portfolio: boolean
  /** The intake record the portfolio was read from — the create route's key. */
  source_ingestion_id: string | null
  /** Properties the package lists that no child deal exists for yet. */
  listed_properties: string[]
  active_count: number
  total_count: number
  summary: string
  children: PortfolioChild[]
}

/** A property to add to a deal. Only the name is required. */
export interface NewPropertyInput {
  name: string
  city?: string
  state?: string
  address?: string
  /** The deal's own workbooks about to be filed under the new property: when the deal becomes a
   *  portfolio they are not filed under its first property on the way. */
  excludeVersionIds?: string[]
}

/** What adding a property answers. */
export interface AddedProperties {
  parentId: string
  isPortfolio: true
  /** The deal became a portfolio with this request: its own property is now the first. */
  converted: boolean
  /** The property the request named, whether it created it or it was already there. */
  propertyId: string | null
  /** The portfolio's live properties, in the order they were created. */
  properties: { id: string; name: string; location: string | null }[]
  /** The properties this request created. */
  created: string[]
  /** The deal's own models filed under its first property when it became a portfolio. */
  filed: { versionId: string; modelId: string; propertyId: string; role: string; filename: string }[]
  /** Which of the deal's own models stayed on the deal, and why. */
  notice: string | null
}

/** The portfolio summary's query: the key the Property tab's PortfolioPropertiesPanel reads it under. */
export const LOAN_PORTFOLIO_KEY = (loanId: string) => ['loan-portfolio', loanId] as const

export const portfolioService = {
  get: (loanId: string): Promise<PortfolioSummary> =>
    api.get(`/loans/${loanId}/portfolio`).then((r) => r.data as PortfolioSummary),

  /** Create the confirmed properties as child deals. Idempotent by name. */
  materialize: (ingestionId: string, constituents: string[], note?: string) =>
    api
      .post(`/ingestion/${ingestionId}/materialize-portfolio`, { constituents, note })
      .then((r) => r.data as { parent_loan_id: string; created_child_ids: string[] }),

  /** Add a property to the deal, making it a portfolio if it is not one. Idempotent by name. */
  addProperty: (loanId: string, input: NewPropertyInput): Promise<AddedProperties> =>
    api.post(`/loans/${loanId}/properties`, input).then((r) => r.data as AddedProperties),
}

/**
 * Everything adding a property changes: the deal record (it may now be a portfolio, and
 * its own models' figures may have moved to its first property), the pipeline row, the
 * journal, the portfolio summary, and every workbook list, section and roll-up (they share
 * one key prefix, see `UW_PORTFOLIO_KEY`).
 */
export function refreshAfterPropertyAdded(queryClient: QueryClient, loanId: string) {
  return Promise.all([
    invalidateLoanQueries(queryClient, loanId),
    queryClient.invalidateQueries({ queryKey: LOAN_PORTFOLIO_KEY(loanId) }),
    queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks'] }),
  ])
}
